import { useEffect, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageContent,
} from "beproduct-ui";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/status-badge";
import { PageHeader } from "@/components/navigation/page-header";
import { durationLabel, isoSeconds } from "@/lib/format";

interface RunDetailResponse {
  run: {
    id: string;
    job_name: string | null;
    tenant_name: string;
    tenant_id: string;
    trigger: string;
    status: string;
    started_at: string | null;
    finished_at: string | null;
    error_message: string | null;
  };
  steps: Array<{
    step_index: number;
    module_key: string;
    module_label: string;
    status: string;
    started_at: string | null;
    finished_at: string | null;
    result: unknown;
    error_message: string | null;
    log_blob: string | null;
  }>;
}

const LIVE_STATUSES = new Set(["running", "queued", "cancelling"]);

export const Route = createFileRoute("/_authed/$companySlug/runs/$runId")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  component: RunDetailPage,
});

function RunDetailPage() {
  const { runId } = Route.useParams();
  const qc = useQueryClient();

  const { data, error, isLoading } = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api<RunDetailResponse>(`/api/runs/${runId}`),
    refetchInterval: (q) => {
      const status = (q.state.data as RunDetailResponse | undefined)?.run.status;
      return status && LIVE_STATUSES.has(status) ? 2_000 : false;
    },
  });

  const cancel = useMutation({
    mutationFn: () => api(`/api/runs/${runId}/cancel`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["run", runId] }),
  });

  if (error) {
    return (
      <PageContent>
        <div className="p-8 text-destructive">Failed to load run.</div>
      </PageContent>
    );
  }
  if (isLoading || !data) {
    return (
      <PageContent>
        <div className="p-8 text-muted-foreground">Loading…</div>
      </PageContent>
    );
  }

  const { run, steps } = data;
  const isLive = LIVE_STATUSES.has(run.status);

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-6">
        <PageHeader
          title={run.job_name ?? "ad-hoc run"}
          subtitle={<>{run.tenant_name} · trigger: {run.trigger}</>}
          status={<span className="inline-flex items-center gap-2"><StatusBadge status={run.status} />{isLive && <span className="whitespace-nowrap text-xs text-muted-foreground">auto-refresh 2s</span>}</span>}
          actions={(run.status === "running" || run.status === "queued") && <Button variant="outline" onClick={() => cancel.mutate()}>Cancel</Button>}
        />

        <div className="grid grid-cols-3 gap-4">
          <SmallCard title="Started" value={isoSeconds(run.started_at)} />
          <SmallCard title="Finished" value={isoSeconds(run.finished_at)} />
          <SmallCard title="Duration" value={durationLabel(run.started_at, run.finished_at)} />
        </div>

        {run.error_message && (
          <Card>
            <CardHeader className="pb-2"><CardTitle>Error</CardTitle></CardHeader>
            <CardContent>
              <pre className="text-xs whitespace-pre-wrap text-destructive">{run.error_message}</pre>
            </CardContent>
          </Card>
        )}

        <div>
          <h2 className="text-lg font-semibold mb-3">Steps ({steps.length})</h2>
          <Card>
            <CardContent className="p-0">
              {steps.map((s) => (
                <details
                  key={s.step_index}
                  className="border-b last:border-0"
                  open={s.status === "running" || s.status === "failed"}
                >
                  <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-muted/50">
                    <span className="text-xs text-muted-foreground tabular-nums">#{s.step_index}</span>
                    <span className="font-medium flex-1">{s.module_label}</span>
                    <span className="text-xs text-muted-foreground">
                      {s.started_at ? durationLabel(s.started_at, s.finished_at) : null}
                    </span>
                    <StatusBadge status={s.status} />
                  </summary>
                  <div className="p-3 space-y-3 bg-muted/20">
                    {s.error_message && (
                      <div>
                        <div className="text-xs font-medium text-destructive mb-1">Error</div>
                        <pre className="text-xs whitespace-pre-wrap text-destructive">{s.error_message}</pre>
                      </div>
                    )}
                    {s.result ? (
                      <pre className="text-xs whitespace-pre-wrap bg-background p-2 rounded border">
                        {JSON.stringify(s.result, null, 2)}
                      </pre>
                    ) : null}
                    {s.log_blob ? (
                      <LogPane blob={s.log_blob} live={s.status === "running"} />
                    ) : null}
                  </div>
                </details>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContent>
  );
}

function SmallCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{title}</CardTitle></CardHeader>
      <CardContent><div className="text-sm">{value}</div></CardContent>
    </Card>
  );
}

function LogPane({ blob, live }: { blob: string; live: boolean }) {
  const ref = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (live && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [blob, live]);
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-1">
        Logs ({new Blob([blob]).size} bytes)
        {live && <span className="ml-2 text-info">· live</span>}
      </div>
      <pre
        ref={ref}
        className="text-xs whitespace-pre-wrap bg-foreground text-background p-3 rounded max-h-96 overflow-auto font-mono"
      >
        {blob}
      </pre>
    </div>
  );
}
